/* eslint-disable @typescript-eslint/no-explicit-any */
import { ViewPlugin, Decoration, DecorationSet, EditorView, ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';
import { parser } from 'lezer-jmespath';
import { styleTags, tags } from '@lezer/highlight';
import { autocompletion } from '@codemirror/autocomplete';
import { search } from 'jmespath';

import {
  continuedIndent,
  indentNodeProp,
  foldNodeProp,
  foldInside,
  LRLanguage,
  LanguageSupport,
} from '@codemirror/language';

interface JsonData {
  [key: string]: any;
}

const isValidPath = (obj: any, path: string): boolean => {
  // Split the path by '.' to get the individual keys
  const keys = path.split('.');

  // Iterate through the keys and check if they exist in the object
  for (const key of keys) {
    // Check if the key exists in the object
    if (obj !== null && typeof obj === 'object' && key in obj) {
      // Move to the next level of the object
      obj = obj[key];
    } else {
      // If the key does not exist, return false
      return false;
    }
  }

  // If all keys exist, return true
  return true;
};

const parseDynamicVariable = (
  obj: any,
  path: string
): 'valid-expression-variable' | 'invalid-expression-variable' | 'warn-expression-variable' => {
  try {
    const result = search(obj, path);

    // all dynamic variables must inclue a "." to be valid
    if ((result?.length === 0 || result === null) && isValidPath(obj, path)) return 'valid-expression-variable';
    if (result?.length === 0 || result === null) return 'warn-expression-variable';
    if (result !== undefined) return 'valid-expression-variable';
    return 'invalid-expression-variable';
  } catch (error) {
    return 'invalid-expression-variable';
  }
};

class VariableHighlighter {
  decorations: DecorationSet;

  constructor(view: EditorView, private jsonData: JsonData) {
    this.decorations = this.createDecorations(view);
  }

  update(update: ViewUpdate) {
    if (update.docChanged || update.viewportChanged) {
      this.decorations = this.createDecorations(update.view);
    }
  }

  createDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const text = view.state.doc.toString();
    const regex = /\{\{\s*(.*?)\s*\}\}/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text))) {
      const [fullMatch, varName] = match;
      const from = match.index,
        to = from + fullMatch.length;

      builder.add(
        from,
        to,
        Decoration.mark({
          class: parseDynamicVariable(this.jsonData, varName),
        })
      );
    }

    return builder.finish();
  }
}

// Create a factory function to pass jsonData
function createHighlightVariablesPlugin(jsonData: JsonData): ViewPlugin<VariableHighlighter> {
  return ViewPlugin.fromClass(
    class extends VariableHighlighter {
      constructor(view: EditorView) {
        super(view, jsonData);
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  );
}

export function variableHighlighter(jsonData: JsonData): ViewPlugin<VariableHighlighter>[] {
  return [createHighlightVariablesPlugin(jsonData)];
}

// Extend the language with custom highlighting for variables
const jmespathParser = parser.configure({
  props: [
    styleTags({
      VariableName: tags.keyword,
      '{{}}': tags.special(tags.variableName),
    }),
    indentNodeProp.add({
      MultiSelectHash: continuedIndent({ except: /^\s*\}/ }),
      MultiSelectList: continuedIndent({ except: /^\s*\]/ }),
    }),
    foldNodeProp.add({
      'MultiSelectHash MultiSelectList': foldInside,
    }),
  ],
});

export const jmespathLanguage = LRLanguage.define({
  name: 'jmespath',
  parser: jmespathParser,
  languageData: {
    closeBrackets: { brackets: ['[', '{', '"'] },
    indentOnInput: /^\s*[\}\]]$/,
  },
});

export function jmespathSupport() {
  return new LanguageSupport(jmespathLanguage);
}

const customCompletionSource = (options: string[]) => {
  return (context: any) => {
    const cursor = context.state.selection.main.head;
    const line = context.state.doc.lineAt(cursor);
    const lineTextUpToCursor = line.text.substring(0, cursor - line.from);

    // Regex to capture preceding whitespace
    const match = /(\s*){{\s*[\w\.]*$/.exec(lineTextUpToCursor);
    if (!match) return null;

    let from = cursor - match[0].length + match[1].length;
    const matchText = match[0].replace(/^\s*{{\s*/, '');
    const precedingWhitespace = match[1]; // Capture the preceding whitespace

    // Re-evaluate if we're within an already started "{{" construct
    const lineTextBeforeMatch = lineTextUpToCursor.substring(0, from - precedingWhitespace.length);
    const alreadyStartsWithBraces = lineTextBeforeMatch.endsWith('{{ ');

    if (alreadyStartsWithBraces) {
      // If we detected an additional "{{" before the current match
      const adjustment = lineTextBeforeMatch.lastIndexOf('{{ ');
      if (adjustment !== -1) {
        from = line.from + adjustment + 2; // +2 to account for "{{" length
      }
    }

    // Check if closing brackets already exist
    const remainingText = line.text.substring(cursor - line.from);
    const hasClosingBrackets = remainingText.startsWith('}}');

    const filteredSuggestions = options
      .filter((s: string) => s.toLowerCase().includes(matchText.toLowerCase()))
      .map((s: string) => {
        const prepend = alreadyStartsWithBraces ? '' : '{{ ';
        const append = hasClosingBrackets ? '' : ' }}';

        return {
          label: s,
          apply: (view: EditorView, completion: any, from: number, to: number) => {
            view.dispatch({
              changes: { from: from, to: to, insert: prepend + s + append },
            });
          },
          type: 'variable',
        };
      });

    return { from, options: filteredSuggestions, filter: false };
  };
};

export function variableAutocomplete(options: string[]) {
  return [
    autocompletion({
      override: [customCompletionSource(options)],
    }),
  ];
}

const needsQuoting = (key: string) => /[-. \[\](){}@]/.test(key);
const quoteKey = (key: string) => (needsQuoting(key) ? `"${key}"` : key);

export const getVariableOptions = (obj: any, parentKey = '') => {
  const keys: string[] = [];

  function isPrimitive(value: any) {
    return value !== Object(value);
  }

  function traverse(o: any, parentKey: string) {
    if (Array.isArray(o)) {
      if (parentKey) {
        keys.push(parentKey);

        if (o.length > 0 && typeof o[0] === 'object') {
          const commonProps = new Set<string>();
          o.forEach((item) => {
            if (typeof item === 'object' && item !== null) {
              Object.keys(item).forEach((key) => commonProps.add(key));
            }
          });

          commonProps.forEach((prop) => {
            const quotedProp = quoteKey(prop);
            keys.push(`${parentKey}[].${quotedProp}`);
          });
        }
      }

      o.forEach((item, index) => {
        const arrayPath = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
        if (isPrimitive(item)) {
          keys.push(arrayPath);
        } else {
          traverse(item, arrayPath);
        }
      });
      return;
    }

    for (const key in o) {
      if (o.hasOwnProperty(key)) {
        const quotedKey = quoteKey(key);
        const fullKey = parentKey ? `${parentKey}.${quotedKey}` : quotedKey;

        if (isPrimitive(o[key])) {
          keys.push(fullKey);
        } else {
          traverse(o[key], fullKey);
        }
      }
    }

    if (parentKey && parentKey.includes('.')) {
      keys.push(parentKey);
    }
  }

  traverse(obj, parentKey);
  return keys;
};

export const calculateEditorHeight = (view: EditorView, minRows = 1, maxRows = 12, verticalPadding = 12) => {
  const rowHeight = 28;
  const contentHeight = view.contentHeight + verticalPadding;
  const minHeight = rowHeight * minRows + verticalPadding;
  const maxHeight = rowHeight * maxRows + verticalPadding;

  return Math.max(minHeight, Math.min(maxHeight, contentHeight)) + 'px';
};
